import { FC, useEffect, useCallback } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { useLocation } from 'react-router-dom'

/* Utils */
import {
  ChartData,
  CompetitorAnalysisData,
  CPISeverity,
  DataStatus,
  DetailedComparisonData,
  TrendsDataPayload
} from 'models'

/* Components */
import { BenchmarkingTabWrapper, loadingWrapperSx } from './benchmarkingTab.styles'
import CompetitorAnalysis from './components/CompetitorAnalysis'
import DetailedComparison from './components/DetailedComparison'
import axios from 'lib/axios'
import { normalizeMetricStatus } from 'lib/metricStatus'
import { getPayloadTimePeriod } from '../../../library/utils'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'

interface Props {
  competitorAnalysisData: CompetitorAnalysisData | null
  detailedComparisonData: DetailedComparisonData | null
}

const BenchmarkingTab: FC<Props> = ({ competitorAnalysisData, detailedComparisonData }) => {
  const {
    setCriticalSla,
    selectedFrequency,
    selectedTimePeriod,
    selectedSeverity,
    cpi,
    initialLoad,
    manifestData,
    criticalSla,
    showMessage,
    setShowMessage,
    recalculatingCpiName,
    setRecalculatingCpiName,
    selectedDataSourceIds,
    active,
    isSnowflake,
    dataSourceOptions
  } = useLibraryDetails()
  if (!manifestData) return null
  const unit = manifestData.ui.trend_chart.user_input.visualization_param.unit
  const benchmarkingTabLoading = !initialLoad
  const location = useLocation()

  const fetchV1Data = useCallback(async () => {
    if (!cpi) return
    setCriticalSla(undefined)

    const timePeriod = getPayloadTimePeriod(selectedTimePeriod)
    const payload: TrendsDataPayload = {
      section: selectedSeverity.toLowerCase() as any,
      integration_ids: selectedDataSourceIds[0] === '' ? [''] : selectedDataSourceIds, // configured integration id
      frequency: selectedFrequency,
      timePeriod
    }

    let severities: any = []
    const userInput = manifestData.ui.trend_chart.user_input
    if (userInput.formula_params[0]?.sections.length > 0) {
      severities = userInput.formula_params[0]?.sections
    } else if (userInput.visualization_param.sections.length > 1) {
      severities = userInput.visualization_param.sections
    }

    const newSection = severities.find((item: { display_name: CPISeverity }) => item.display_name === payload.section)
    if (newSection) payload.section = newSection.name

    try {
      const res = await axios.post(`/api/v3/cpis/${cpi?.name}/data`, payload)

      if (res.status === 201) {
        const { data }: { data: ChartData } = res.data
        data.status = normalizeMetricStatus(data.status)
        setCriticalSla(data.sla)
        const isRecalculating = data.status === DataStatus.Recalculating
        if (recalculatingCpiName === cpi?.name) {
          setShowMessage(isRecalculating)
          if (!isRecalculating) setRecalculatingCpiName(null)
        }
      }
    } catch (err) {
      console.error(err)
    }
  }, [cpi, selectedTimePeriod, manifestData, selectedSeverity, selectedDataSourceIds, selectedFrequency, recalculatingCpiName])

  const fetchSnowflakeData = async () => {
    const timePeriod = getPayloadTimePeriod(selectedTimePeriod)
    const instanceNames = dataSourceOptions
      .filter((c) => selectedDataSourceIds.includes(c.name || ''))
      .map((c) => c.instance_name || '')
    const snowflakePayload = {
      metric_request: {
        section: selectedSeverity.toLowerCase() as any,
        frequency: selectedFrequency,
        instance_names: instanceNames,
        time_period: timePeriod
      }
    }

    const res = await axios.post(`/api/v4/metrics/${cpi?.name}/timeseries?trends-benchmarking`, snowflakePayload)

    if (res.status === 200) {
      const { data }: { data: ChartData } = res.data
      data.status = normalizeMetricStatus(data.status)
      setCriticalSla(data.sla)
      const isRecalculating = data.status === DataStatus.Recalculating
      if (recalculatingCpiName === cpi?.name) {
        setShowMessage(isRecalculating)
        if (!isRecalculating) setRecalculatingCpiName(null)
      }
    }
  }

  const fetchSla = async () => {
    if (isSnowflake) {
      await fetchSnowflakeData()
    } else {
      await fetchV1Data()
    }
  }

  useEffect(() => {
    if (cpi && manifestData) {
      if (recalculatingCpiName !== cpi.name) {
        setShowMessage(false)
      }
      fetchSla().catch((e) => e)
    }
  }, [cpi, manifestData, location])

  if (benchmarkingTabLoading) {
    return (
      <Box sx={loadingWrapperSx}>
        <CircularProgress size="36px" />
      </Box>
    )
  }
  return (
    <BenchmarkingTabWrapper>
      {competitorAnalysisData && (
        <CompetitorAnalysis
          showMessage={showMessage}
          data={competitorAnalysisData}
          sla={criticalSla}
          unit={unit}
          active={Boolean(active)}
        />
      )}
      {active && detailedComparisonData && (
        <DetailedComparison data={detailedComparisonData} showMessage={showMessage} />
      )}
    </BenchmarkingTabWrapper>
  )
}

export default BenchmarkingTab
